/**
 * LEOS Mission Ready - Main Application Entry Point
 * Orchestrates the complete user experience from login to Mission Ready interface
 */

// Set Mission Ready mode flag immediately to prevent app.js from auto-initializing
window.missionReadyMode = true;

import { unifiedAuth } from './auth/unified-auth.js';
import { missionInterface } from './mission-ready/main-interface.js';

// Import the existing LEOS components we need
import { initApp as initLEOSCore } from './app.js';

class LEOSMissionReadyApp {
    constructor() {
        this.isInitialized = false;
        this.coreInitialized = false;
    }

    async init() {
        console.log('Initializing LEOS Mission Ready...');
        
        // Add CSS files
        this.loadStylesheets();
        
        // Check for existing session
        const existingUser = unifiedAuth.checkExistingSession();
        
        if (existingUser) {
            console.log('Found existing session for:', existingUser.name);
            
            // Initialize mission start time
            window.missionStartTime = new Date();
            
            // Skip login and go directly to Mission Ready
            this.enterMissionReady();
        } else {
            // Show login screen if no existing session
            unifiedAuth.showLoginScreen();
            
            // Set up callback for after authentication
            window.onAuthComplete = (user) => {
                console.log('Authentication complete for:', user.name);
                
                // Initialize mission start time when user logs in
                window.missionStartTime = new Date();
                
                this.enterMissionReady();
            };
        }
        
        this.isInitialized = true;
    }

    loadStylesheets() {
        const stylesheets = [
            'css/auth-system.css',
            'css/mission-ready.css'
        ];
        
        stylesheets.forEach(href => {
            if (!document.querySelector(`link[href="${href}"]`)) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = href;
                document.head.appendChild(link);
            }
        });
    }

    async enterMissionReady() {
        console.log('Entering Mission Ready interface...');
        
        // Initialize Mission Ready interface first
        missionInterface.init();
        
        // Start core initialization in the background
        if (!this.coreInitialized) {
            this.initializeCore().then(() => {
                console.log('Core initialization complete');
                // Set up global handlers for integration after core is ready
                this.setupIntegration();
                
                // Complete loading animation then remove screen
                if (window.completeLoading) {
                    window.completeLoading();
                }
                
                // Remove loading screen after a brief delay
                setTimeout(() => {
                    const loadingOverlay = document.getElementById('loading-overlay');
                    if (loadingOverlay) {
                        loadingOverlay.style.opacity = '0';
                        setTimeout(() => loadingOverlay.remove(), 300);
                    }
                    
                    // Show CapCom button now that everything is loaded
                    this.showCapComButton();
                }, 1000);
            });
        } else {
            // If already initialized, just set up integration
            this.setupIntegration();
            
            // Remove loading screen
            const loadingOverlay = document.getElementById('loading-overlay');
            if (loadingOverlay) {
                loadingOverlay.style.opacity = '0';
                setTimeout(() => {
                    loadingOverlay.remove();
                    // Show CapCom button now that everything is loaded
                    this.showCapComButton();
                }, 300);
            }
        }
    }

    async initializeCore() {
        console.log('Initializing LEOS core systems...');
        
        try {
            // Initialize the core LEOS app but don't show the old UI
            document.body.classList.add('mission-ready-mode');
            
            // Initialize core without the brand UI
            await initLEOSCore();
            
            // Initialize time controls after core is loaded
            if (window.setupTimeControls) {
                window.setupTimeControls();
            }
            
            this.coreInitialized = true;
            console.log('LEOS core systems initialized');
            
        } catch (error) {
            console.error('Error initializing LEOS core:', error);
        }
    }

    setupIntegration() {
        // Integration between Mission Ready interface and LEOS core
        
        // Time multiplier integration
        window.setTimeMultiplier = (multiplier) => {
            if (window.simState) {
                window.simState.timeMultiplier = multiplier;
            }
        };
        
        // SDA toggle integration
        window.toggleSDA = () => {
            const sdaToggleBtn = document.getElementById('sda-toggle-btn');
            if (sdaToggleBtn) {
                sdaToggleBtn.click();
            } else if (window.sdaController) {
                window.sdaController.toggle();
            }
        };
        
        // Data source manager integration
        window.openDataManager = () => {
            const event = new KeyboardEvent('keydown', {
                key: 'd',
                ctrlKey: true,
                bubbles: true
            });
            document.dispatchEvent(event);
        };
        
        // TLE addition integration
        window.addTLE = () => {
            const addTLEBtn = document.getElementById('add-tle-button');
            if (addTLEBtn) {
                addTLEBtn.click();
            } else if (window.createTLEInputModal) {
                window.createTLEInputModal();
            }
        };
        
        // Settings integration
        window.openSettings = () => {
            const settingsBtn = document.getElementById('simulation-settings-btn');
            if (settingsBtn) {
                settingsBtn.click();
            }
        };
        
        // Hide old UI elements when in Mission Ready mode
        this.hideOldUI();
        
        // Also hide the old time display in the top bar
        const timeDisplayContainer = document.getElementById('time-display-container');
        if (timeDisplayContainer) {
            timeDisplayContainer.style.display = 'none';
        }
        const timeDisplay = document.getElementById('time-display');
        if (timeDisplay) {
            timeDisplay.style.display = 'none';
        }
        
        // Set up canvas resize handling
        window.addEventListener('resize', () => {
            if (window.engine) {
                window.engine.resize();
            }
        });
    }

    showCapComButton() {
        // Show CapCom button after simulation is fully loaded
        const capcomBtn = document.getElementById('capcom-kelly-btn');
        if (capcomBtn) {
            capcomBtn.style.display = 'flex';
            console.log('CapCom button now visible');
        }
    }

    hideOldUI() {
        // Hide all the old First Orbit UI elements
        const oldUIElements = [
            'help-sphere-btn',
            'sda-toggle-btn',
            'simulation-settings-btn', 
            'add-tle-button',
            'telemetry-dashboard-btn',
            'data-source-btn',
            'leos-dial',
            'kelcodes',
            'time-display-container',
            'welcome-modal',
            'loading-screen',
            'control-dock-container',
            'control-dock',
            'minimized-dock',
            'learning-btn',
            'support-btn',
            'dock-minimize-btn',
            'cyberrts-logo-link'
        ];
        
        // Hide immediately and set up observer for dynamically created elements
        const hideElements = () => {
            oldUIElements.forEach(id => {
                const element = document.getElementById(id);
                if (element) {
                    element.style.display = 'none';
                }
            });
        };
        
        hideElements();
        
        // Set up mutation observer to hide elements as they're created
        const observer = new MutationObserver(() => {
            hideElements();
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // Utility methods for the Mission Ready interface
    static showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `mr-notification ${type}`;
        
        const iconSvg = type === 'success' ? 'sat.svg' : type === 'error' ? 'support.svg' : 'telem.svg';
        
        notification.innerHTML = `
            <div class="notification-content">
                <span class="notification-icon">
                    <img src="assets/${iconSvg}" alt="${type}" style="width: 16px; height: 16px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                </span>
                <span class="notification-message">${message}</span>
            </div>
        `;
        
        // Add notification styles if not already present
        if (!document.querySelector('#notification-styles')) {
            const style = document.createElement('style');
            style.id = 'notification-styles';
            style.textContent = `
                .mr-notification {
                    position: fixed;
                    top: 100px;
                    right: 32px;
                    background: rgba(15, 23, 42, 0.95);
                    border: 1px solid rgba(148, 163, 184, 0.2);
                    border-radius: 12px;
                    padding: 16px 20px;
                    color: #e2e8f0;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                    backdrop-filter: blur(10px);
                    z-index: 10001;
                    animation: slideInRight 0.3s ease;
                    max-width: 400px;
                }
                
                .mr-notification.success {
                    border-color: rgba(16, 185, 129, 0.3);
                }
                
                .mr-notification.error {
                    border-color: rgba(239, 68, 68, 0.3);
                }
                
                .notification-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .notification-message {
                    font-size: 14px;
                    font-weight: 500;
                }
                
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `;
            document.head.appendChild(style);
        }
        
        document.body.appendChild(notification);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            notification.style.animation = 'slideInRight 0.3s ease reverse';
            setTimeout(() => notification.remove(), 300);
        }, 5000);
    }
}

// Hide old UI elements immediately on script load
(function() {
    const style = document.createElement('style');
    style.textContent = `
        #loading-screen,
        #learning-btn,
        #support-btn,
        #control-dock-container,
        #control-dock,
        #minimized-dock {
            display: none !important;
        }
    `;
    document.head.appendChild(style);
})();

// Initialize the application when DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
    const app = new LEOSMissionReadyApp();
    await app.init();
    
    // Make app globally available for debugging
    window.missionReadyApp = app;
    
    // Export notification utility
    window.showMRNotification = LEOSMissionReadyApp.showNotification;
});

export { LEOSMissionReadyApp };